/**
 * PatientInvoicesList Component
 *
 * List of patient invoices with empty state and load more functionality.
 */

import { KeyboardArrowDown,ReceiptLong as NoReceiptIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { InvoiceResponse } from '@store/generated/payments';

import { PatientInvoiceCard } from './PatientInvoiceCard';

export interface PatientInvoicesListProps {
  invoices: InvoiceResponse[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onView: (invoice: InvoiceResponse) => void;
  onDownload: (invoice: InvoiceResponse) => void;
  onLoadMore: () => void;
  /** Invoice ID + action currently loading a fresh URL */
  loadingInvoice?: { id: string; action: 'view' | 'download' } | null;
}

/**
 * List of patient invoices with empty state and load more
 */
export function PatientInvoicesList({
  invoices,
  hasMore,
  isLoadingMore,
  onView,
  onDownload,
  onLoadMore,
  loadingInvoice,
}: PatientInvoicesListProps) {
  if (invoices.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
          textAlign: 'center',
        }}
      >
        <NoReceiptIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary">
          No invoices found for this patient.
        </Typography>
      </Box>
    );
  }

  // Sort invoices by date (newest first)
  const sortedInvoices = [...invoices].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <Stack spacing={1}>
      {sortedInvoices.map(invoice => (
        <PatientInvoiceCard
          key={invoice.id}
          invoice={invoice}
          onView={onView}
          onDownload={onDownload}
          loadingAction={
            loadingInvoice?.id === invoice.id ? loadingInvoice.action : null
          }
        />
      ))}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            startIcon={
              isLoadingMore ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <KeyboardArrowDown />
              )
            }
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

export default PatientInvoicesList;
